import { useEffect, useRef } from 'react'

/**
 * Hook to trap focus within a modal/dialog
 * @param {boolean} isOpen - Whether the modal is open
 * @returns {Object} ref - Ref to attach to the modal container
 */
export function useFocusTrap (isOpen) {
    const containerRef = useRef(null)
    const previousActiveElement = useRef(null)

    useEffect(() => {
        if (!isOpen) return

        // Store the currently focused element
        previousActiveElement.current = document.activeElement

        const container = containerRef.current
        if (!container) return

        // Get all focusable elements within the container
        const getFocusableElements = () => {
            return container.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        }

        // Move focus to the first focusable element
        const focusableElements = getFocusableElements()
        if (focusableElements.length > 0) {
            focusableElements[0].focus()
        }

        // Handle tab key to trap focus
        const handleKeyDown = e => {
            if (e.key !== 'Tab') return

            const focusableElements = getFocusableElements()
            const firstElement = focusableElements[0]
            const lastElement = focusableElements[focusableElements.length - 1]

            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstElement) {
                    e.preventDefault()
                    lastElement.focus()
                }
            } else {
                // Tab
                if (document.activeElement === lastElement) {
                    e.preventDefault()
                    firstElement.focus()
                }
            }
        }

        container.addEventListener('keydown', handleKeyDown)

        // Cleanup
        return () => {
            container.removeEventListener('keydown', handleKeyDown)

            // Restore focus to the previously focused element
            if (previousActiveElement.current) {
                previousActiveElement.current.focus()
            }
        }
    }, [isOpen])

    return containerRef
}
